import { useRef } from 'react';
import { ca } from '../i18n/ca';
import { toMetres } from './anchorMath';

const t = ca.catalogPage2.library.workshop;

/** One anchor on the sketch: a spot in the kit's frame (metres, the middle at 0,0, north up, the gate facing east). */
export interface SketchAnchor {
  name: string;
  index: number;
  x: number;
  y: number;
  azimuth: number;
  placed: boolean;
}

const STEP = 1;
const BIG_STEP = 5;
const round1 = (v: number) => Math.round(v * 10) / 10;

/**
 * The sketch of a composition with its functional anchors
 * (docs/design/COMPOSITION_LIBRARY.md §5.1): every piece a small square at its
 * offset, the footprint's circle, and a handle for each anchor that can be
 * dragged, or focused and moved with the arrow keys. An anchor with nothing
 * proposed is drawn dashed at the middle until it is placed.
 */
export function AnchorSketch({
  objects,
  anchors,
  footprint,
  size = 360,
  editable = false,
  onMove,
}: {
  objects: { className: string; offset: number[] }[];
  anchors: SketchAnchor[];
  footprint: number;
  size?: number;
  editable?: boolean;
  onMove?: (name: string, index: number, x: number, y: number) => void;
}) {
  const svg = useRef<SVGSVGElement>(null);
  const dragging = useRef<{ name: string; index: number } | null>(null);

  const reach = Math.max(
    footprint,
    ...objects.map((o) => Math.hypot(o.offset[0] ?? 0, o.offset[1] ?? 0)),
    ...anchors.map((a) => Math.hypot(a.x, a.y)),
    8,
  );
  const extent = Math.ceil(reach) + 6;
  const label = (a: SketchAnchor) =>
    `${t.anchorNames[a.name] ?? a.name}${a.name === 'park' ? ` ${a.index + 1}` : ''}`;

  const move = (a: SketchAnchor, x: number, y: number) =>
    onMove?.(a.name, a.index, round1(x), round1(y));

  return (
    <svg
      ref={svg}
      className={`anchor-sketch ${editable ? 'editable' : ''}`}
      width={size}
      height={size}
      viewBox={`${-extent} ${-extent} ${2 * extent} ${2 * extent}`}
      role="group"
      aria-label={t.sketch}
      onPointerMove={(e) => {
        const d = dragging.current;
        const el = svg.current;
        if (!d || !el) return;
        const at = toMetres(e.clientX, e.clientY, el.getBoundingClientRect(), extent);
        if (at) onMove?.(d.name, d.index, round1(at.x), round1(at.y));
      }}
      onPointerUp={() => {
        dragging.current = null;
      }}
      onPointerCancel={() => {
        dragging.current = null;
      }}
      onPointerLeave={() => {
        dragging.current = null;
      }}
    >
      <circle cx={0} cy={0} r={Math.max(1, footprint)} className="edge" />
      <line x1={0} y1={0} x2={Math.max(1, footprint)} y2={0} className="front" />
      {objects.map((o, i) => (
        <rect
          key={i}
          className="piece"
          x={(o.offset[0] ?? 0) - 0.6}
          // The map's y grows north and the picture's grows down.
          y={-(o.offset[1] ?? 0) - 0.6}
          width={1.2}
          height={1.2}
        />
      ))}
      {anchors.map((a) => {
        const rad = (a.azimuth * Math.PI) / 180;
        return (
          <g
            key={`${a.name}-${a.index}`}
            className={`anchor anchor-${a.name} ${a.placed ? '' : 'to-place'}`}
            transform={`translate(${a.x} ${-a.y})`}
            tabIndex={editable ? 0 : -1}
            role={editable ? 'button' : 'img'}
            aria-label={`${label(a)}${a.placed ? '' : ` (${t.toPlace})`}`}
            data-anchor={a.name}
            data-index={a.index}
            onPointerDown={(e) => {
              if (!editable) return;
              dragging.current = { name: a.name, index: a.index };
              (e.target as Element).setPointerCapture?.(e.pointerId);
              e.preventDefault();
            }}
            onKeyDown={(e) => {
              if (!editable) return;
              const step = e.shiftKey ? BIG_STEP : STEP;
              const dx = e.key === 'ArrowRight' ? step : e.key === 'ArrowLeft' ? -step : 0;
              const dy = e.key === 'ArrowUp' ? step : e.key === 'ArrowDown' ? -step : 0;
              if (dx === 0 && dy === 0) return;
              e.preventDefault();
              move(a, a.x + dx, a.y + dy);
            }}
          >
            <circle r={1.9} />
            <line x1={0} y1={0} x2={Math.sin(rad) * 3.6} y2={-Math.cos(rad) * 3.6} />
            <text x={2.5} y={0.9}>
              {label(a)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
